/*
Purpose: Move a deferred student onto another session time and date.
Project: TCMS

Two steps in one dialog: pick a target session time, then pick one of that template's
upcoming dates. Both lists come from the backend, so nothing is guessed client-side.
*/

import React from 'react';
import PropTypes from 'prop-types';
import { Modal, Table } from 'react-bootstrap';
import Button from '@mui/material/Button';
import Select from 'react-select';
import Moment from 'moment';

/** `YYYY-MM-DD` to `DD-MM-YYYY`, or a dash when absent. */
function displayDate(value) {
  if (!value) {
    return '-';
  }
  const parsed = Moment(value, 'YYYY-MM-DD');
  return parsed.isValid() ? parsed.format('DD-MM-YYYY') : '-';
}

/** `HH:mm:ss` to `HH:mm`, or a dash when absent. */
function displayTime(value) {
  return value ? String(value).slice(0, 5) : '-';
}

export default function ReEnrolModal({
  show,
  student,
  sessionOptions,
  selectedSession,
  dateOptions,
  selectedDate,
  isSubmitting,
  onSelectSession,
  onSelectDate,
  onCancel,
  onConfirm,
}) {
  const hasSessions = sessionOptions.length > 0;

  return (
    <Modal show={show} onHide={onCancel} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">Re-enrol {student ? student.name : 'student'}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {!hasSessions ? (
          <p className="mb-0 text-muted">
            No alternative session times are available for this student.
          </p>
        ) : null}

        {hasSessions && !selectedSession ? (
          <>
            <h6 className="form-lable">Choose a session time</h6>
            <div style={{ maxHeight: 320, overflow: 'auto' }}>
              <Table hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Session</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Time</th>
                    <th>Tutor</th>
                    <th aria-label="Select" />
                  </tr>
                </thead>
                <tbody>
                  {sessionOptions.map((option) => (
                    <tr key={option.pk_sest_key}>
                      <td>{option.sest_name || '-'}</td>
                      <td>{displayDate(option.sest_from_date)}</td>
                      <td>{displayDate(option.sest_to_date)}</td>
                      <td>
                        {displayTime(option.sest_start_time)} – {displayTime(option.sest_end_time)}
                      </td>
                      <td>{[option.tut_fname, option.tut_surname].filter(Boolean).join(' ') || '-'}</td>
                      <td>
                        <Button
                          variant="contained"
                          className="primary-filled"
                          style={{ fontSize: 12 }}
                          onClick={() => onSelectSession(option)}
                        >
                          Select
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        ) : null}

        {selectedSession ? (
          <>
            <div className="mb-3">
              <span className="text-muted">Moving from</span>
              <div className="fw-bold">
                {student && student.currentSessionName ? student.currentSessionName : '—'}
              </div>
            </div>

            <div className="mb-3">
              <span className="text-muted">Re-enrol into</span>
              <div className="fw-bold">{selectedSession.sest_name || '—'}</div>
            </div>

            <h6 className="form-lable required-field">Re-enrol date</h6>
            <Select
              menuPlacement="auto"
              options={dateOptions}
              value={dateOptions.find((option) => option.value === selectedDate) || null}
              onChange={(option) => onSelectDate(option ? option.value : '')}
              placeholder={dateOptions.length ? '--Select--' : 'No upcoming dates'}
              isDisabled={dateOptions.length === 0}
            />
          </>
        ) : null}
      </Modal.Body>

      <Modal.Footer className="pt-0">
        <div className="mx-auto d-flex gap-2">
          <Button variant="contained" className="primary-gray" onClick={onCancel}>
            Cancel
          </Button>
          {selectedSession ? (
            <Button
              variant="contained"
              className="primary-filled"
              onClick={onConfirm}
              disabled={isSubmitting || !selectedDate}
            >
              Confirm re-enrol
            </Button>
          ) : null}
        </div>
      </Modal.Footer>
    </Modal>
  );
}

ReEnrolModal.propTypes = {
  show: PropTypes.bool.isRequired,
  student: PropTypes.shape({
    name: PropTypes.string,
    currentSessionName: PropTypes.string,
  }),
  sessionOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedSession: PropTypes.object,
  dateOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  selectedDate: PropTypes.string,
  isSubmitting: PropTypes.bool,
  onSelectSession: PropTypes.func.isRequired,
  onSelectDate: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

ReEnrolModal.defaultProps = {
  student: null,
  selectedSession: null,
  selectedDate: '',
  isSubmitting: false,
};
